import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const ContentContext = createContext();

const PLACEHOLDER_IMAGE = 'https://via.placeholder.com/150';

export const ContentProvider = ({ children }) => {
  const [domain, setDomain] = useState('https://newsbyte.com.au');
  const [siteCredential, setSiteCredential] = useState({url: '', username: '', password: ''});
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [blogs, setBlogs] = useState([]);
  const [error, setError] = useState(null);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingPosts, setLoadingPosts] = useState(false);

  // Fetch categories from API when domain changes
  useEffect(() => {
    if (domain) {
      setLoadingCategories(true);
      setError(null);
      axios.get(`${domain}/wp-json/wp/v2/categories?per_page=100`)
        .then((response) => {
          setCategories(response.data);
          setSelectedCategory('');
          setBlogs([]);
        })
        .catch(() => {
          setError('Failed to fetch categories. Please check the domain or try again later.');
          setCategories([]);
          setSelectedCategory('');
          setBlogs([]);
        })
        .finally(() => {
          setLoadingCategories(false);
        });
    } else {
      setCategories([]);
      setSelectedCategory('');
      setBlogs([]);
      setError(null);
    }
  }, [domain]);

  // Helper to fetch media details for a post's featured_media
  const fetchMediaDetails = async (mediaId) => {
    if (!mediaId) return { source_url: PLACEHOLDER_IMAGE, thumbnail_url: PLACEHOLDER_IMAGE };
    try {
      const response = await axios.get(`${domain}/wp-json/wp/v2/media/${mediaId}`);
      return {
        source_url: response.data.source_url || PLACEHOLDER_IMAGE,
        thumbnail_url: response.data.media_details?.sizes?.thumbnail?.source_url || PLACEHOLDER_IMAGE,
      };
    } catch {
      return { source_url: PLACEHOLDER_IMAGE, thumbnail_url: PLACEHOLDER_IMAGE };
    }
  };

  // Fetch posts for selected category
  useEffect(() => {
    if (selectedCategory) {
      setLoadingPosts(true);
      setError(null);
      axios.get(`${domain}/wp-json/wp/v2/posts?per_page=100&categories=${selectedCategory}`)
        .then(async (response) => {
          const postsWithMedia = await Promise.all(response.data.map(async (post) => {
            const media = await fetchMediaDetails(post.featured_media);
            return {
              id: post.id,
              title: post.title.rendered,
              url: post.link,
              content: post.content.rendered,
              wordCount: post.content.rendered.split(' ').length,
              featured_media_url: media.source_url,
              featured_media_thumbnail: media.thumbnail_url,
            };
          }));
          setBlogs(postsWithMedia);
        })
        .catch(() => {
          setError('Failed to fetch posts for the selected category.');
          setBlogs([]);
        })
        .finally(() => {
          setLoadingPosts(false);
        });
    } else {
      setBlogs([]);
      setError(null);
    }
  }, [selectedCategory, domain]);

  return (
    <ContentContext.Provider
      value={{
        domain,
        siteCredential,
        setSiteCredential,
        setDomain,
        categories,
        selectedCategory,
        setSelectedCategory,
        blogs,
        error,
        setError,
        loadingCategories,
        loadingPosts,
      }}
    >
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = () => useContext(ContentContext);
